import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as productApi from '../../store/api/products';

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await productApi.getProducts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch products');
    }
  }
);

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductDetails(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch product details');
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await productApi.getFeaturedProducts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch featured products');
    }
  }
);

export const fetchNewArrivals = createAsyncThunk(
  'products/fetchNewArrivals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await productApi.getNewArrivals(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch new arrivals');
    }
  }
);

export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchProductsByCategory',
  async ({ category, params = {} }, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductsByCategory(category, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch products by category');
    }
  }
);

export const fetchProductsByBrand = createAsyncThunk(
  'products/fetchProductsByBrand',
  async ({ brand, params = {} }, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductsByBrand(brand, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch products by brand');
    }
  }
);

export const fetchRelatedProducts = createAsyncThunk(
  'products/fetchRelatedProducts',
  async ({ productId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await productApi.getRelatedProducts(productId, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch related products');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productApi.getCategories();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch categories');
    }
  }
);

export const fetchProductColors = createAsyncThunk(
  'products/fetchProductColors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductColors();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch product colors');
    }
  }
);

export const searchProductsAsync = createAsyncThunk(
  'products/searchProducts',
  async ({ query, params = {} }, { rejectWithValue }) => {
    try {
      const response = await productApi.searchProducts(query, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to search products');
    }
  }
);

// Initial state
const initialState = {
  products: [],
  featuredProducts: [],
  newArrivals: [],
  productDetail: null,
  relatedProducts: [],
  categoryProducts: {},
  brandProducts: {},
  searchResults: {
    query: '',
    products: [],
    count: 0
  },
  categories: [],
  colors: [],
  pagination: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 12
  },
  filters: {
    category: '',
    brand: '',
    color: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  loading: {
    products: false,
    productDetail: false,
    featured: false,
    newArrivals: false,
    categoryProducts: false,
    brandProducts: false,
    relatedProducts: false,
    categories: false,
    colors: false,
    search: false
  },
  error: {
    products: null,
    productDetail: null,
    featured: null,
    newArrivals: null,
    categoryProducts: null,
    brandProducts: null,
    relatedProducts: null,
    categories: null,
    colors: null,
    search: null
  }
};

// Slice
const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProductDetail: (state) => {
      state.productDetail = null;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
    },
    resetFilters: (state) => {
      state.filters = {
        category: '',
        brand: '',
        color: '',
        minPrice: '',
        maxPrice: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
    }
  },
  extraReducers: (builder) => {
    // Fetch products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading.products = true;
        state.error.products = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload.data.products;
        state.pagination = action.payload.data.pagination;
        state.loading.products = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading.products = false;
        const e1 = action.payload; state.error.products = typeof e1 === 'string' ? e1 : (e1?.message || 'Failed to fetch products');
      });

    // Fetch product details
    builder
      .addCase(fetchProductDetails.pending, (state) => {
        state.loading.productDetail = true;
        state.error.productDetail = null;
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.productDetail = action.payload.data.product;
        state.loading.productDetail = false;
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loading.productDetail = false;
        const e2 = action.payload; state.error.productDetail = typeof e2 === 'string' ? e2 : (e2?.message || 'Failed to fetch product details');
      });

    // Fetch featured products
    builder
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.loading.featured = true;
        state.error.featured = null;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.featuredProducts = action.payload.data.products;
        state.loading.featured = false;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.loading.featured = false;
        const e3 = action.payload; state.error.featured = typeof e3 === 'string' ? e3 : (e3?.message || 'Failed to fetch featured products');
      });

    // Fetch new arrivals
    builder
      .addCase(fetchNewArrivals.pending, (state) => {
        state.loading.newArrivals = true;
        state.error.newArrivals = null;
      })
      .addCase(fetchNewArrivals.fulfilled, (state, action) => {
        state.newArrivals = action.payload.data.products;
        state.loading.newArrivals = false;
      })
      .addCase(fetchNewArrivals.rejected, (state, action) => {
        state.loading.newArrivals = false;
        const err = action.payload; state.error.newArrivals = typeof err === 'string' ? err : (err?.message || 'Failed to fetch new arrivals');
      });

    // Fetch products by category
    builder
      .addCase(fetchProductsByCategory.pending, (state) => {
        state.loading.categoryProducts = true;
        state.error.categoryProducts = null;
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        const { category, products, pagination } = action.payload.data;
        state.categoryProducts[category] = products;
        state.pagination = pagination;
        state.loading.categoryProducts = false;
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        state.loading.categoryProducts = false;
        const e4 = action.payload; state.error.categoryProducts = typeof e4 === 'string' ? e4 : (e4?.message || 'Failed to fetch products by category');
      });

    // Fetch products by brand
    builder
      .addCase(fetchProductsByBrand.pending, (state) => {
        state.loading.brandProducts = true;
        state.error.brandProducts = null;
      })
      .addCase(fetchProductsByBrand.fulfilled, (state, action) => {
        const { brand, products, pagination } = action.payload.data;
        state.brandProducts[brand] = products;
        state.pagination = pagination;
        state.loading.brandProducts = false;
      })
      .addCase(fetchProductsByBrand.rejected, (state, action) => {
        state.loading.brandProducts = false;
        state.error.brandProducts = action.payload || 'Failed to fetch products by brand';
      });

    // Fetch related products
    builder
      .addCase(fetchRelatedProducts.pending, (state) => {
        state.loading.relatedProducts = true;
        state.error.relatedProducts = null;
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.relatedProducts = action.payload.data.products;
        state.loading.relatedProducts = false;
      })
      .addCase(fetchRelatedProducts.rejected, (state, action) => {
        state.loading.relatedProducts = false;
        const e5 = action.payload; state.error.relatedProducts = typeof e5 === 'string' ? e5 : (e5?.message || 'Failed to fetch related products');
      });

    // Fetch categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading.categories = true;
        state.error.categories = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data.categories;
        state.loading.categories = false;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading.categories = false;
        state.error.categories = action.payload || 'Failed to fetch categories';
      });

    // Fetch product colors
    builder
      .addCase(fetchProductColors.pending, (state) => {
        state.loading.colors = true;
        state.error.colors = null;
      })
      .addCase(fetchProductColors.fulfilled, (state, action) => {
        state.colors = action.payload.data.colors;
        state.loading.colors = false;
      })
      .addCase(fetchProductColors.rejected, (state, action) => {
        state.loading.colors = false;
        state.error.colors = action.payload || 'Failed to fetch product colors';
      });

    // Search products
    builder
  .addCase(searchProductsAsync.pending, (state) => {
    state.loading.search = true;
    state.error.search = null;
  })
  .addCase(searchProductsAsync.fulfilled, (state, action) => {
    state.searchResults = {
      query: action.payload.data.query,
      products: action.payload.data.products,  // ✅ Fixed: Changed from 'results' to 'products'
      count: action.payload.data.count
    };
    state.pagination = action.payload.data.pagination; // ✅ Also store pagination
    state.loading.search = false;
  })
  .addCase(searchProductsAsync.rejected, (state, action) => {
    state.loading.search = false;
    state.error.search = action.payload || 'Failed to search products';
  });
  }
});

export const { 
  clearProductDetail, 
  setPage, 
  setFilters, 
  resetFilters 
} = productSlice.actions;

export default productSlice.reducer;